"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { startAuthentication } from "@simplewebauthn/browser";
import type {
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { apiGetQuiet, apiPost, apiPostQuiet } from "@/lib/api";

/**
 * Login is two steps for anyone with a second factor armed.
 *
 * `login()` returns `"second-factor"` when the password was accepted but a
 * second factor is still owed, along with the ones this user has — a passkey
 * (Touch ID / Face ID), a 6-digit code, or both. It returns `"done"` when the
 * user has no second factor and is already signed in (possibly with onboarding
 * flags that force /setup).
 *
 * Between the two calls the browser holds only a short-lived `otp_pending`
 * cookie, which the backend refuses to accept as a session — so a half-finished
 * login grants nothing.
 */
export type SecondFactor = "passkey" | "totp";

export type LoginResult =
  | { result: "done"; mustChangePassword: boolean; mustSetup2fa: boolean }
  | { result: "second-factor"; methods: SecondFactor[] };

/** The passkey challenge the backend hands out. webauthn-rs wraps its options
 *  in `publicKey`; the browser library wants that inner object. */
export interface PasskeyLoginStart {
  stateId: string;
  options: { publicKey: PublicKeyCredentialRequestOptionsJSON };
}

interface MeResponse {
  username: string;
  must_change_password?: boolean;
  must_setup_2fa?: boolean;
  /** Real admin when viewing as another user. */
  impersonator?: string | null;
  /** Whether the real signed-in principal is in the sudo group. */
  is_sudo?: boolean;
  /** Whether the effective (viewed) user is sudo — false when viewing as a normal user. */
  effective_is_sudo?: boolean;
}

/** True when the device prompt was dismissed by the user (or timed out) rather
 *  than genuinely failing. The library wraps the browser error, so look at
 *  `cause` as well as `name`. */
export function isPasskeyCancel(err: unknown): boolean {
  const names = [
    err instanceof Error ? err.name : "",
    err instanceof Error && err.cause instanceof Error ? err.cause.name : "",
  ];
  return names.includes("NotAllowedError") || names.includes("AbortError");
}

/** Browser text, not a guess. NotAllowedError is also thrown when the click
 *  was spent on a network call, or when a phone QR is treated as a duplicate. */
export function passkeyErrorMessage(err: unknown): string {
  if (!(err instanceof Error)) return "Failed to add passkey";
  const extra =
    err.cause instanceof Error && err.cause.message && err.cause.message !== err.message
      ? ` — ${err.cause.message}`
      : "";
  return `${err.message}${extra}`;
}

/** Windows/Linux (and library PCs): the phone QR only appears if the browser
 *  prompt runs inside a click, not after the password request returns. */
export function passkeyNeedsClick(): boolean {
  if (typeof navigator === "undefined") return true;
  return !/iPhone|iPad|Macintosh/i.test(navigator.userAgent);
}

interface AuthContextValue {
  user: string | null;
  loading: boolean;
  mustChangePassword: boolean;
  mustSetup2fa: boolean;
  needsSetup: boolean;
  /** Real admin username when impersonating another user. */
  impersonator: string | null;
  /** True when the real principal is in the sudo group (admin chrome stays on). */
  isSudo: boolean;
  /** True when the effective/viewed user is sudo. Code columns and similar use this. */
  effectiveIsSudo: boolean;
  login: (username: string, password: string) => Promise<LoginResult>;
  verifyOtp: (code: string) => Promise<{ mustChangePassword: boolean; mustSetup2fa: boolean }>;
  beginPasskeyLogin: () => Promise<PasskeyLoginStart>;
  loginWithPasskey: (prefetched?: PasskeyLoginStart) => Promise<{ mustChangePassword: boolean; mustSetup2fa: boolean }>;
  refreshMe: () => Promise<void>;
  setOnboardingFlags: (mustChangePassword: boolean, mustSetup2fa: boolean) => void;
  /** Admin: view the app as another user (data-scoped). */
  impersonate: (username: string) => Promise<void>;
  /** Admin: leave impersonation and restore your own session. */
  stopImpersonate: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  mustChangePassword: false,
  mustSetup2fa: false,
  needsSetup: false,
  impersonator: null,
  isSudo: false,
  effectiveIsSudo: false,
  login: async () => ({ result: "done", mustChangePassword: false, mustSetup2fa: false }),
  verifyOtp: async () => ({ mustChangePassword: false, mustSetup2fa: false }),
  beginPasskeyLogin: async () => ({ stateId: "", options: { publicKey: {} as PublicKeyCredentialRequestOptionsJSON } }),
  loginWithPasskey: async () => ({ mustChangePassword: false, mustSetup2fa: false }),
  refreshMe: async () => {},
  setOnboardingFlags: () => {},
  impersonate: async () => {},
  stopImpersonate: async () => {},
  logout: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

function flagsFrom(res: MeResponse) {
  return {
    mustChangePassword: !!res.must_change_password,
    mustSetup2fa: !!res.must_setup_2fa,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [mustSetup2fa, setMustSetup2fa] = useState(false);
  const [impersonator, setImpersonator] = useState<string | null>(null);
  const [isSudo, setIsSudo] = useState(false);
  const [effectiveIsSudo, setEffectiveIsSudo] = useState(false);

  const applyMe = useCallback((res: MeResponse) => {
    setUser(res.username);
    setImpersonator(res.impersonator ?? null);
    setIsSudo(!!res.is_sudo);
    // Prefer effective_is_sudo when present; fall back to is_sudo for older backends.
    // (Backend always sends the field now, including false under impersonation.)
    setEffectiveIsSudo(
      typeof res.effective_is_sudo === "boolean" ? res.effective_is_sudo : !!res.is_sudo,
    );
    const f = flagsFrom(res);
    setMustChangePassword(f.mustChangePassword);
    setMustSetup2fa(f.mustSetup2fa);
    return f;
  }, []);

  const clearAuth = useCallback(() => {
    setUser(null);
    setMustChangePassword(false);
    setMustSetup2fa(false);
    setImpersonator(null);
    setIsSudo(false);
    setEffectiveIsSudo(false);
  }, []);

  const refreshMe = useCallback(async () => {
    const res = await apiGetQuiet<MeResponse>("/auth/me");
    applyMe(res);
  }, [applyMe]);

  useEffect(() => {
    apiGetQuiet<MeResponse>("/auth/me")
      .then((res) => applyMe(res))
      .catch(() => clearAuth())
      .finally(() => setLoading(false));
  }, [applyMe, clearAuth]);

  const login = useCallback(async (username: string, password: string): Promise<LoginResult> => {
    const res = await apiPostQuiet<MeResponse & {
      otp_required?: boolean;
      methods?: SecondFactor[];
    }>("/auth/login", { username, password });
    if (res.otp_required) {
      // Deliberately do NOT set the user here — the session does not exist yet.
      return { result: "second-factor", methods: res.methods ?? ["totp"] };
    }
    const f = applyMe(res);
    return { result: "done", ...f };
  }, [applyMe]);

  const verifyOtp = useCallback(async (code: string) => {
    const res = await apiPostQuiet<MeResponse>("/auth/verify-otp", { code });
    return applyMe(res);
  }, [applyMe]);

  const beginPasskeyLogin = useCallback(async () => {
    return apiPostQuiet<PasskeyLoginStart>("/auth/passkey/login/start", {});
  }, []);

  /** Start → device prompt → finish. Pass a prefetched start so Windows/Linux
   *  can call the prompt in the same click (the phone QR will not appear without that). */
  const loginWithPasskey = useCallback(async (prefetched?: PasskeyLoginStart) => {
    const start = prefetched ?? await beginPasskeyLogin();
    const optionsJSON: PublicKeyCredentialRequestOptionsJSON = {
      ...start.options.publicKey,
    };
    if (passkeyNeedsClick()) {
      optionsJSON.hints = ["hybrid"];
    }
    const credential = await startAuthentication({ optionsJSON });
    const res = await apiPostQuiet<MeResponse>("/auth/passkey/login/finish", {
      state_id: start.stateId,
      credential,
    });
    return applyMe(res);
  }, [applyMe, beginPasskeyLogin]);

  const setOnboardingFlags = useCallback((mcp: boolean, m2fa: boolean) => {
    setMustChangePassword(mcp);
    setMustSetup2fa(m2fa);
  }, []);

  const impersonate = useCallback(async (username: string) => {
    const res = await apiPost<MeResponse>("/auth/impersonate", { username });
    applyMe(res);
  }, [applyMe]);

  const stopImpersonate = useCallback(async () => {
    const res = await apiPost<MeResponse>("/auth/impersonate/stop", {});
    applyMe(res);
  }, [applyMe]);

  const logout = useCallback(async () => {
    await apiPost("/auth/logout", {});
    clearAuth();
  }, [clearAuth]);

  const needsSetup = mustChangePassword || mustSetup2fa;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        mustChangePassword,
        mustSetup2fa,
        needsSetup,
        impersonator,
        isSudo,
        effectiveIsSudo,
        login,
        verifyOtp,
        beginPasskeyLogin,
        loginWithPasskey,
        refreshMe,
        setOnboardingFlags,
        impersonate,
        stopImpersonate,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
